/**
 * Image URLs from a v1 `WireAsset`. Every asset carries `src`, a template with literal
 * `{crop}`, `{w}` and `{fmt}` tokens served by the CMS `/i/` route (bkfc-cms
 * docs/image-resizing.md §1-2, docs/routes/domain.md "Every WireAsset now carries src").
 * `url` is the original source, for share/download only: never displayed.
 */

/** The closed width ladder `/i/` renders; anything else is a 404. */
export const IMAGE_WIDTHS = [160, 240, 320, 480, 640, 960, 1280, 1920, 2560, 3840];

/** The smallest rung at or above `px`, capped at the top rung. */
export const snapWidth = px =>
  IMAGE_WIDTHS.find(w => w >= px) ?? IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1];

/**
 * One rendition of `asset`, or null when there is no asset or no template.
 * @param {{src?: string} | null | undefined} asset
 * @param {'16x9' | '2x3' | '1x1' | 'fit'} crop the slot's shape; `fit` for any other shape
 * @param {number} width slot layout px x device pixel ratio, snapped up to the ladder
 */
export function imageUrl(asset, crop, width) {
  if (!asset?.src) {
    return null;
  }
  return asset.src
    .replace('{crop}', crop)
    .replace('{w}', String(snapWidth(width)))
    .replace('{fmt}', 'webp');
}

/** A `srcset` over the ladder up to `maxWidth` (web emits a srcset rather than one rung). */
export function imageSrcSet(asset, crop, maxWidth = 1920) {
  if (!asset?.src) {
    return undefined;
  }
  return IMAGE_WIDTHS.filter(w => w <= maxWidth)
    .map(w => `${imageUrl(asset, crop, w)} ${w}w`)
    .join(', ');
}
